import { URLS } from 'store/slices/constants/apiV1';
import { apiSlice } from 'store/slices/api';

// Velt-specific types matching the backend serializers
export interface VeltAttachment {
  attachmentId: string;
  name: string | null;
  url: string;
}

export interface VeltComment {
  commentId: string | number; // Velt expects string | number
  commentText: string | null;
  commentHtml: string | null;
  attachments: Record<string, VeltAttachment>;
}

export interface PartialCommentAnnotation {
  annotationId: string;
  metadata?: {
    document_id?: string;
    folder_id?: string;
    location_id?: string;
    // Metadata is for data coming from Velt and we don't have consistent typing for it
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  comments: Record<string, VeltComment>;
}

// Response type for GET endpoint
export type CommentAnnotationsResponse = {
  data: Record<string, PartialCommentAnnotation>;
};

// Request types for mutations
export interface CreateCommentRequest {
  user: string; // Format: "{org_id}_{user_id}"
  comment_annotation: {
    [annotationId: string]: PartialCommentAnnotation;
  };
}

export interface CreateCommentResponse {
  created_comment_annotation_ids: string[];
  created_comment_ids: string[];
}

export interface UpdateCommentResponse {
  updated_comment_annotation_ids: string[];
  updated_comment_ids: string[];
}

export interface DeleteCommentRequest {
  user: string; // Format: "{org_id}_{user_id}"
  comment_annotation: {
    [annotationId: string]: {
      annotationId: string;
      comments: {
        [commentId: string]: {
          commentId: string;
        };
      };
    };
  };
}

export interface AttachmentData {
  id: string;
  file: File;
  created_by: {
    id: string;
    first_name: string;
    last_name: string;
    title: string;
    email: string;
    image: string;
    is_staff: boolean;
  };
  org: {
    id: string;
    name: string;
    slug: string;
    org_code: string;
  };
  name?: string;
  mime_type?: string;
  document_id?: string;
  folder_id?: string;
  comment?: string;
  // Metadata is for data coming from Velt and we don't have consistent typing for it
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
}

export const notificationExtendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCommentAnnotations: builder.query<
      CommentAnnotationsResponse,
      { documentIds?: string[]; locationIds?: string[]; folderIds?: string[] }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.documentIds?.length) {
          queryParams.append('document_ids', params.documentIds.join(','));
        }
        if (params.locationIds?.length) {
          queryParams.append('location_ids', params.locationIds.join(','));
        }
        if (params.folderIds?.length) {
          queryParams.append('folder_ids', params.folderIds.join(','));
        }
        return `${URLS.USER_NOTIFICATION_COMMENTS}?${queryParams}`;
      },
      providesTags: ['CommentAnnotations'],
    }),
    saveComment: builder.mutation<
      CreateCommentResponse | UpdateCommentResponse,
      CreateCommentRequest
    >({
      query: (payload) => ({
        url: URLS.USER_NOTIFICATION_COMMENTS,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['CommentAnnotations'],
    }),
    deleteComment: builder.mutation<void, DeleteCommentRequest>({
      query: (payload) => ({
        url: URLS.USER_NOTIFICATION_COMMENTS,
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: ['CommentAnnotations'],
    }),
    saveAttachment: builder.mutation<AttachmentData, FormData>({
      query: (formData) => ({
        url: URLS.USER_NOTIFICATION_ATTACHMENTS,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['CommentAnnotations'],
    }),
    deleteAttachment: builder.mutation<void, { id: string }>({
      query: (payload) => ({
        url: URLS.USER_NOTIFICATION_ATTACHMENTS,
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: ['CommentAnnotations'],
    }),
  }),
});

export const {
  useGetCommentAnnotationsQuery,
  useSaveCommentMutation,
  useDeleteCommentMutation,
  useSaveAttachmentMutation,
  useDeleteAttachmentMutation,
} = notificationExtendedApiSlice;
